import { useEffect, useState } from 'react'

/**
 * Calls `onLoadMore` when a sentinel element (rendered after the list) enters the viewport.
 *
 * A sentinel + IntersectionObserver is used instead of an `onScroll` handler because
 * client-side exclusion filters can thin a page so much that the scroll box never overflows,
 * so no scroll event would ever fire. The observer is re-created each time `canLoadMore` flips
 * back to true; its initial callback reports the sentinel's current visibility, so pages keep
 * loading until the box fills or no pages remain.
 *
 * The node is held in state (callback ref) so the effect re-runs when the sentinel mounts late,
 * e.g. inside Antd Modal content that renders only after the modal opens.
 *
 * @param {boolean} canLoadMore - true when another page may be requested right now
 * @param {() => void} onLoadMore - stable callback, e.g. react-query `fetchNextPage`
 * @returns {(node: Element | null) => void} stable callback ref for the sentinel element
 */
export default function useInfiniteScrollSentinel(canLoadMore, onLoadMore) {
  const [node, setNode] = useState(null)

  useEffect(() => {
    if (!node || !canLoadMore) return
    if (typeof window.IntersectionObserver === 'undefined') return

    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadMore()
    })
    observer.observe(node)

    return () => observer.disconnect()
  }, [node, canLoadMore, onLoadMore])

  return setNode
}
